/**
 * themed-dialog.jsx — Iter 191v
 *
 * Promise-based, portal-mounted replacements for `window.confirm` and
 * `window.prompt`.  Match the dark-violet glassmorphism aesthetic of the
 * website builder so we never fall back to the browser's white system UI.
 *
 * Usage:
 *   import { themedConfirm, themedPrompt } from '@/lib/themed-dialog';
 *
 *   if (!(await themedConfirm({ message: 'Delete this block?', danger: true }))) return;
 *   const name = await themedPrompt({ message: 'Name your new fund', initialValue: 'Scholarship Fund' });
 *   if (!name) return;
 *
 * Both functions return Promises:
 *   themedConfirm(...)  →  Promise<boolean>
 *   themedPrompt(...)   →  Promise<string | null>  (null = cancelled)
 *
 * Keyboard: Enter = confirm, Escape = cancel.  Backdrop click = cancel.
 */
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { AlertCircle, X as XIcon } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────
// Portal host
// ─────────────────────────────────────────────────────────────────────────

function ensureRoot() {
  let node = document.getElementById('themed-dialog-root');
  if (!node) {
    node = document.createElement('div');
    node.id = 'themed-dialog-root';
    document.body.appendChild(node);
  }
  return node;
}

// ─────────────────────────────────────────────────────────────────────────
// Rendering shell
// ─────────────────────────────────────────────────────────────────────────

function DialogShell({
  title, message, danger, confirmLabel, cancelLabel,
  onConfirm, onCancel,
  input, initialValue, placeholder, inputType, submitLabel,
}) {
  const [value, setValue] = useState(initialValue || '');
  const inputRef = useRef(null);

  useEffect(() => {
    // Focus the input if prompt, else the confirm button
    if (input && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select?.();
    }
  }, [input]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
      else if (e.key === 'Enter' && !e.shiftKey) {
        // Enter accepts everywhere except a multi-line textarea (we don't use one).
        e.preventDefault();
        input ? onConfirm(value) : onConfirm(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [input, value, onConfirm, onCancel]);

  const primaryClass = danger
    ? 'bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-400 hover:to-rose-500'
    : 'bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] hover:from-[#A78BFA] hover:to-[#60A5FA]';

  return (
    <div
      className="fixed inset-0 z-[9999] grid place-items-center px-4"
      style={{ animation: 'themed-fade-in 0.15s ease-out' }}
      data-testid="themed-dialog"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      {/* backdrop */}
      <div className="absolute inset-0 bg-[#08081A]/75 backdrop-blur-md" />

      {/* card */}
      <div
        role="dialog"
        aria-modal="true"
        className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[#171A2C]/95 shadow-[0_25px_60px_-15px_rgba(139,92,246,0.35)] overflow-hidden"
        style={{ animation: 'themed-scale-in 0.18s cubic-bezier(0.16, 1, 0.3, 1)' }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* soft top accent */}
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#A78BFA]/60 to-transparent" />
        <button
          type="button"
          onClick={onCancel}
          className="absolute top-3 right-3 w-8 h-8 grid place-items-center rounded-lg text-white/50 hover:text-white hover:bg-white/[0.06] transition-colors"
          aria-label="Close"
          data-testid="themed-dialog-close"
        >
          <XIcon size={14} />
        </button>

        <div className="p-6 pt-7">
          <div className="flex items-start gap-3 mb-3">
            {danger && (
              <div className="w-9 h-9 rounded-xl grid place-items-center bg-red-500/15 border border-red-500/25 flex-shrink-0 mt-0.5">
                <AlertCircle size={16} className="text-red-300" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              {title && (
                <h2 className="text-white font-bold text-lg leading-snug" style={{ fontFamily: 'Syne, Inter, sans-serif' }} data-testid="themed-dialog-title">
                  {title}
                </h2>
              )}
              {message && (
                <p className={`text-white/70 text-sm leading-relaxed ${title ? 'mt-1.5' : ''}`} data-testid="themed-dialog-message">
                  {message}
                </p>
              )}
            </div>
          </div>

          {input && (
            <input
              ref={inputRef}
              type={inputType || 'text'}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={placeholder}
              className="mt-4 w-full px-3.5 py-2.5 rounded-xl bg-white/[0.05] border border-white/10 text-white placeholder:text-[#64748B] text-sm focus:border-[#A78BFA] focus:ring-2 focus:ring-[#A78BFA]/25 outline-none transition-all"
              data-testid="themed-dialog-input"
            />
          )}

          <div className="mt-6 flex justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 rounded-xl text-[11.5px] font-bold uppercase tracking-[0.14em] bg-white/[0.06] hover:bg-white/[0.1] text-white/85 border border-white/10 transition-colors"
              data-testid="themed-dialog-cancel"
            >
              {cancelLabel || 'Cancel'}
            </button>
            <button
              type="button"
              onClick={() => (input ? onConfirm(value) : onConfirm(true))}
              className={`px-5 py-2 rounded-xl text-[11.5px] font-bold uppercase tracking-[0.14em] text-white transition-all shadow-[0_0_20px_rgba(139,92,246,0.28)] hover:shadow-[0_0_28px_rgba(139,92,246,0.45)] ${primaryClass}`}
              data-testid="themed-dialog-confirm"
              disabled={input && !value.trim()}
            >
              {input ? (submitLabel || 'Save') : (confirmLabel || 'Confirm')}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes themed-fade-in { from { opacity: 0 } to { opacity: 1 } }
        @keyframes themed-scale-in { from { opacity: 0; transform: scale(0.94) translateY(6px); } to { opacity: 1; transform: scale(1) translateY(0); } }
      `}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Imperative API
// ─────────────────────────────────────────────────────────────────────────

function mountDialog(props) {
  const container = document.createElement('div');
  ensureRoot().appendChild(container);
  const root = ReactDOM.createRoot(container);

  return new Promise((resolve) => {
    const unmount = (value) => {
      root.unmount();
      container.remove();
      resolve(value);
    };

    root.render(
      <DialogShell
        {...props}
        onConfirm={(v) => unmount(v)}
        onCancel={() => unmount(props.input ? null : false)}
      />,
    );
  });
}

/**
 * Themed replacement for `window.confirm`.
 * Accepts either an options object OR a plain message string (with an
 * optional second `title` string for parity with common patterns).
 * @returns {Promise<boolean>}
 */
export function themedConfirm(opts, maybeTitle) {
  const config = typeof opts === 'string'
    ? { message: opts, title: maybeTitle }
    : (opts || {});
  return mountDialog({
    title:        config.title,
    message:      config.message || 'Are you sure?',
    danger:       !!config.danger,
    confirmLabel: config.confirmLabel,
    cancelLabel:  config.cancelLabel,
    input:        false,
  });
}

/**
 * Themed replacement for `window.prompt`.
 * Accepts either an options object OR the classic `(message, initialValue)`
 * pair so existing call-sites stay identical.
 * @returns {Promise<string|null>}  null = cancelled or empty
 */
export function themedPrompt(opts, maybeInitial) {
  const config = typeof opts === 'string'
    ? { message: opts, initialValue: maybeInitial || '' }
    : (opts || {});
  return mountDialog({
    title:        config.title,
    message:      config.message,
    initialValue: config.initialValue || '',
    placeholder:  config.placeholder || '',
    inputType:    config.inputType || 'text',
    submitLabel:  config.submitLabel,
    cancelLabel:  config.cancelLabel,
    input:        true,
  });
}
